// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Pure utility helpers used to build the carousel layout.
 *
 * These functions are kept stateless and free of React imports so they can be
 * referenced from styled-components, JSX, or hooks alike.
 */

import {
    ArrowPosition,
    ArrowType,
    CarouselDirection,
    SlidesPerView,
} from "@stoneage/App/Components/EmblaCarousel/models";
import xScreen from "@stoneage/XDL/Utilities/xScreen";
import { css } from "styled-components";
import breakpointsSystem from "@stoneage/XDL/PreStyle/breakpoints";

/**
 * Resolve the visual position of an arrow given its type and the carousel's
 * scroll direction.
 *
 * @example
 *   getArrowPosition("next", "horizontal") // => "right"
 *   getArrowPosition("prev", "vertical")   // => "top"
 */
export const getArrowPosition = (arrowType: ArrowType, direction: CarouselDirection = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";

    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }

    return isVertical ? "top" : "left";
};

function resolveBreakpointValue(bp: string | number): number {
    if (typeof bp === "number") return bp;
    const index = breakpointsSystem.keys.indexOf(bp);
    return index !== -1 ? breakpointsSystem.values[index] : 0;
}

function sortBreakpointsDescending(
    slidesPerView: Record<string, { slides: number; gap?: number }>,
): Array<[string | number, { slides: number; gap?: number }]> {
    return Object.entries(slidesPerView).sort(([a], [b]) => {
        return resolveBreakpointValue(String(b)) - resolveBreakpointValue(String(a));
    });
}

/**
 * Compute the CSS rule that sizes each slide for the chosen `view` (grid
 * or flex) based on `slidesPerView`.
 *
 * Supports three input shapes:
 * - `"auto"`     → slides size themselves to their content
 * - `number`     → a fixed number of slides per view
 * - object       → a responsive map: `{ md: { slides: 2 }, lg: { slides: 3 } }`
 *
 * The function returns a styled-components `css` value (or an array of them
 * for responsive configs), or an empty string when `slidesPerView` is unset.
 */
export const getSlideWidth = (view: "grid" | "flex", slidesPerView: SlidesPerView | undefined, gap: number) => {
    if (!slidesPerView) return "";

    if (view === "grid") {
        if (slidesPerView === "auto") {
            return css`
                grid-auto-columns: minmax(max-content, 100vw);
            `;
        }

        if (typeof slidesPerView === "number") {
            return css`
                grid-auto-columns: calc((100% - ${gap}px * (${slidesPerView} - 1)) / ${slidesPerView});
            `;
        }

        const sorted = sortBreakpointsDescending(slidesPerView);
        const breakpointsCssStyle = sorted.map(([breakpoint, config], index) => {
            const { slides, gap: bpGap = gap } = config;
            const isLargest = index === 0;

            const gridColumnStyle = css`
                grid-auto-columns: calc((100% - ${bpGap}px * (${slides} - 1)) / ${slides});
                ${bpGap !== undefined &&
                css`
                    column-gap: ${bpGap}px;
                `}
            `;
            if (isLargest) {
                return css`
                    ${gridColumnStyle}
                `;
            }

            return css`
                ${xScreen("lt", breakpoint)} {
                    ${gridColumnStyle}
            `;
        });

        return breakpointsCssStyle;
    }

    // view === "flex"

    if (slidesPerView === "auto") {
        return css`
            flex: 0 0 auto;
        `;
    }

    if (typeof slidesPerView === "number") {
        return css`
            flex: 0 0 calc((100% - ${gap}px * ${slidesPerView - 1}) / ${slidesPerView});
        `;
    }

    return Object.keys(slidesPerView).map(breakpoint => {
        const { slides, gap: bpGap = gap } = slidesPerView[breakpoint];
        return css`
            ${xScreen("lte", breakpoint)} {
                flex: 0 0 calc((100% - ${bpGap}px * ${slides - 1}) / ${slides});
            }
        `;
    });
};
